/**
 * Created by neo on 10.01.2026
 * Challenge My Progress Component - Shows the user's progress in the challenge
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { RouteChallengeTeam } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { IndividualChallengeParticipant } from '../../../Model/Engagement/IndividualChallenge/IndividualChallengeParticipant';
import { RouteChallengeLocation } from '../../../Model/Engagement/RouteChallenge/RouteChallengeLocation';
import { Theme } from '../../../Theme/Theme';
import type { ChallengeType } from '../ChallengeDetailScreen';
import { getMediaSmallest } from '../../../Model/Media/Media';
import { ChallengeExtraMile } from './ChallengeExtraMile';
import { ChallengeTeamMembers } from './ChallengeTeamMembers';

const ProgressContainer = styled.div<{ $goalReached: boolean }>`
  background: ${({ $goalReached }) => $goalReached
    ? Theme.Colors.primaryDarkest
    : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  color: white;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
`;

const StatLabel = styled.div`
  font-size: 12px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProgressBarContainer = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  overflow: hidden;
  height: 12px;
  margin-bottom: 12px;
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  background: white;
  border-radius: 8px;
  width: ${(props) => Math.min(props.progress, 100)}%;
  transition: width 0.5s ease-out;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  opacity: 0.9;
`;

const TeamInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const TeamAvatar = styled.div<{ imageUrl?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(props) => (props.imageUrl ? `url(${props.imageUrl})` : 'rgba(255, 255, 255, 0.2)')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 20px;
  flex-shrink: 0;
`;

const TeamDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const TeamName = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const TeamSubtext = styled.div`
  font-size: 13px;
  opacity: 0.8;
`;

const RankBadge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
`;

export type ChallengeMyProgressProps = {
  challengeType: ChallengeType;
  challenge: RouteChallenge | IndividualChallenge;
  myTeam: RouteChallengeTeam | null;
  myParticipation: IndividualChallengeParticipant | null;
  locations: RouteChallengeLocation[];
};

export const ChallengeMyProgress: React.FC<ChallengeMyProgressProps> = observer(
  ({ challengeType, challenge, myTeam, myParticipation, locations }) => {
    const { t } = useTrans();

    const isIndividual = challengeType === 'individual';
    const totalPoints = isIndividual
      ? myParticipation?.totalCollectedPoints ?? 0
      : myTeam?.totalCollectedPoints ?? 0;
    // Use reachedLocationsCount which excludes the start point
    const reachedLocationsCount = isIndividual
      ? myParticipation?.reachedLocationsCount ?? 0
      : myTeam?.reachedLocationsCount ?? 0;
    // Total locations to reach excludes the start point
    const totalLocationsToReach = Math.max(0, locations.length - 1);
    const rank = isIndividual
      ? myParticipation?.rank
      : myTeam?.globalRank;

    const totalPossiblePoints = challenge.totalPossiblePoints;
    const progress = totalPossiblePoints > 0 ? Math.min(100, (totalPoints / totalPossiblePoints) * 100) : 0;
    const goalReached = challenge.started && totalPossiblePoints > 0 && totalPoints >= totalPossiblePoints;

    return (
      <ProgressContainer $goalReached={goalReached}>
        <SectionTitle>
          <span>🚀</span>
          {t('challenge.myProgress.title', { defaultValue: 'Your Progress' })}
        </SectionTitle>

        <StatsRow>
          <StatItem>
            <StatValue>{totalPoints.toLocaleString()}</StatValue>
            <StatLabel>{t('challenge.myProgress.points', { defaultValue: 'Points' })}</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>
              {reachedLocationsCount}/{totalLocationsToReach}
            </StatValue>
            <StatLabel>{t('challenge.myProgress.locations', { defaultValue: 'Locations' })}</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{rank ? `#${rank}` : '-'}</StatValue>
            <StatLabel>{t('challenge.myProgress.rank', { defaultValue: 'Rank' })}</StatLabel>
          </StatItem>
        </StatsRow>

        <ProgressBarContainer>
          <ProgressBarFill progress={progress} />
        </ProgressBarContainer>

        <ProgressText>
          <span>{Math.round(progress)}% {t('challenge.myProgress.complete', { defaultValue: 'complete' })}</span>
          <span>
            {Math.min(totalPoints, totalPossiblePoints).toLocaleString()} / {totalPossiblePoints.toLocaleString()}{' '}
            {t('challenge.myProgress.pts', { defaultValue: 'pts' })}
          </span>
        </ProgressText>

        <ChallengeExtraMile
          challenge={challenge}
          totalPoints={totalPoints}
          extraPoints={isIndividual ? myParticipation?.extraCollectedPoints : myTeam?.extraCollectedPoints}
          goalPoints={totalPossiblePoints}
        />

        {!isIndividual && myTeam && (
          <TeamInfo>
            <TeamAvatar imageUrl={myTeam.image ? getMediaSmallest(myTeam.image) : undefined}>
              {!myTeam.image && myTeam.name.charAt(0).toUpperCase()}
            </TeamAvatar>
            <TeamDetails>
              <TeamName><ChallengeTeamMembers team={myTeam} challenge={challenge} /></TeamName>
              <TeamSubtext>
                {myTeam.memberCount} {t('challenge.myProgress.members', { defaultValue: 'members' })}
              </TeamSubtext>
            </TeamDetails>
            {myTeam.globalRank && (
              <RankBadge>#{myTeam.globalRank}</RankBadge>
            )}
          </TeamInfo>
        )}
      </ProgressContainer>
    );
  },
);
